import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Edit, Loader2, Navigation, Search, LocateFixed, Star, Building2, Keyboard, Phone, Globe, Clock, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { GoogleMap, useJsApiLoader, Marker, Circle } from "@react-google-maps/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useTheme } from "@/contexts/ThemeContext";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_LIBRARIES = ["places"];

const venueTypes = [
  { value: "restaurant_cafe", label: "Restaurant / Café" },
  { value: "bar_pub", label: "Bar / Pub" },
  { value: "nightclub", label: "Nightclub" },
  { value: "retail", label: "Retail Store" },
  { value: "supermarket", label: "Supermarket" },
  { value: "gym", label: "Gym / Fitness" },
  { value: "hospital_medical", label: "Hospital / Medical Clinic" },
  { value: "automotive", label: "Automotive" },
  { value: "salon", label: "Salon / Beauty" },
  { value: "tattoo", label: "Tattoo Studio" },
  { value: "other", label: "Other" },
];

const australianStates = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "NT", label: "Northern Territory" },
  { value: "ACT", label: "Australian Capital Territory" },
];

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
];

// Light map: keep Google's default light palette but declutter POI/transit labels and
// icons so it matches the clean look of darkMapStyles. Used when the portal is in light mode.
const lightMapStyles = [
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
];

// Clean map options - no Google branding or controls. The `styles` key is overridden per
// render from the portal theme (see mapOptions in the component); darkMapStyles here is
// just the default.
const cleanMapOptions = {
  styles: darkMapStyles,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

export default function AdvertiserLocations() {
  const { advertiser, advertiserId, refreshStats, refreshAdvertiser } = useOutletContext();
  const { token } = useAuth();
  const { theme } = useTheme();
  // Map colour scheme follows the portal light/dark theme instead of being hardcoded dark.
  const mapOptions = useMemo(
    () => ({ ...cleanMapOptions, styles: theme === "dark" ? darkMapStyles : lightMapStyles }),
    [theme]
  );
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [addressSearch, setAddressSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [settingPrimary, setSettingPrimary] = useState(null); // Track which location is being set as primary
  const [entryMode, setEntryMode] = useState("auto"); // "auto" or "manual"

  const [formData, setFormData] = useState({
    name: "",
    street_address: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    country: "Australia",
    latitude: -33.8688,
    longitude: 151.2093,
    radius_meters: 500,
    venue_type: "retail",
    // Places enrichment fields
    phone: null,
    website: null,
    business_status: null,
    rating: null,
    user_ratings_total: null,
    editorial_summary: null,
    opening_hours: null,
    secondary_opening_hours: null,
    google_place_id: null,
    entry_mode: "auto",
    timezone_offset: null,
  });
  const [mapCenter, setMapCenter] = useState({ lat: -33.8688, lng: 151.2093 });
  const overviewMapRef = useRef(null);
  const formMapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: GOOGLE_MAPS_LIBRARIES });

  // Map Google Place types to Booma venue types
  const mapGoogleTypeToVenueType = (types) => {
    const typeMap = {
      cafe: "cafe", coffee_shop: "cafe",
      restaurant: "restaurant_cafe", food: "restaurant_cafe",
      bar: "bar_pub", pub: "bar_pub",
      night_club: "nightclub",
      clothing_store: "retail", shoe_store: "retail", shopping_mall: "retail",
      store: "retail", department_store: "retail", electronics_store: "retail",
      supermarket: "supermarket", grocery_or_supermarket: "supermarket",
      gym: "gym", fitness_center: "gym",
      hospital: "hospital_medical", doctor: "hospital_medical", dentist: "hospital_medical",
      pharmacy: "hospital_medical", physiotherapist: "hospital_medical",
      car_dealer: "automotive", car_repair: "automotive", car_wash: "automotive",
      hair_care: "salon", beauty_salon: "salon", spa: "salon",
      tattoo_parlor: "tattoo",
    };
    for (const t of (types || [])) {
      if (typeMap[t]) return typeMap[t];
    }
    return null;
  };

  // Handle address selection from autocomplete
  const handlePlaceSelect = useCallback((addressData) => {
    const mappedVenueType = mapGoogleTypeToVenueType(addressData.types);
    setFormData(prev => ({
      ...prev,
      name: addressData.place_name || prev.name,
      street_address: addressData.street_address,
      suburb: addressData.suburb,
      state: addressData.state || prev.state,
      postcode: addressData.postcode,
      country: addressData.country,
      latitude: addressData.latitude,
      longitude: addressData.longitude,
      // Places enrichment
      phone: addressData.phone,
      website: addressData.website,
      opening_hours: addressData.opening_hours,
      business_status: addressData.business_status,
      rating: addressData.rating,
      user_ratings_total: addressData.user_ratings_total,
      google_place_id: addressData.place_id,
      venue_type: mappedVenueType || prev.venue_type,
      entry_mode: "auto",
      timezone_offset: addressData.utc_offset_minutes ?? null,
    }));
    setEntryMode("auto");
    setMapCenter({ lat: addressData.latitude, lng: addressData.longitude });
    setAddressSearch(addressData.formatted_address);

    if (formMapRef.current) {
      formMapRef.current.panTo({ lat: addressData.latitude, lng: addressData.longitude });
      formMapRef.current.setZoom(16);
    }

    toast.success("Business details populated from Google Places");
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [advertiserId]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/accounts/${advertiserId}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
      if (response.data.length > 0) {
        setMapCenter({ lat: response.data[0].latitude, lng: response.data[0].longitude });
      }
    } catch (error) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = useMemo(() => {
    if (!searchQuery) return locations;
    const query = searchQuery.toLowerCase();
    return locations.filter(loc => 
      loc.name?.toLowerCase().includes(query) ||
      loc.suburb?.toLowerCase().includes(query) ||
      loc.postcode?.includes(query)
    );
  }, [locations, searchQuery]);

  const handleMapClick = useCallback((e) => {
    setFormData(prev => ({
      ...prev,
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng()
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, account_id: advertiserId };
      if (editingLocation) {
        await axios.put(`${API}/locations/${editingLocation.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/locations`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Location created");
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
      if (refreshStats) refreshStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this location?")) return;
    try {
      await axios.delete(`${API}/locations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Location deleted");
      fetchLocations();
      if (refreshStats) refreshStats();
      if (refreshAdvertiser) refreshAdvertiser(); // Refresh in case primary was deleted
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleSetPrimary = async (locationId) => {
    setSettingPrimary(locationId);
    try {
      await axios.put(`${API}/accounts/${advertiserId}/primary-location/${locationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Primary location set successfully");
      if (refreshAdvertiser) refreshAdvertiser(); // Refresh advertiser data to update primary_location_id
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set primary location");
    } finally {
      setSettingPrimary(null);
    }
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setAddressSearch(`${location.street_address}, ${location.suburb}, ${location.state} ${location.postcode}`);
    setEntryMode(location.entry_mode || (location.google_place_id ? "auto" : "manual"));
    setFormData({
      name: location.name,
      street_address: location.street_address,
      suburb: location.suburb,
      state: location.state,
      postcode: location.postcode,
      country: location.country,
      latitude: location.latitude,
      longitude: location.longitude,
      radius_meters: location.radius_meters,
      venue_type: location.venue_type,
      phone: location.phone || null,
      website: location.website || null,
      business_status: location.business_status || null,
      rating: location.rating || null,
      user_ratings_total: location.user_ratings_total || null,
      editorial_summary: location.editorial_summary || null,
      opening_hours: location.opening_hours || null,
      secondary_opening_hours: location.secondary_opening_hours || null,
      google_place_id: location.google_place_id || null,
      entry_mode: location.entry_mode || "auto",
      timezone_offset: location.timezone_offset || null,
    });
    setMapCenter({ lat: location.latitude, lng: location.longitude });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLocation(null);
    setAddressSearch("");
    setEntryMode("auto");
    setFormData({
      name: "",
      street_address: "",
      suburb: "",
      state: "NSW",
      postcode: "",
      country: "Australia",
      latitude: -33.8688,
      longitude: 151.2093,
      radius_meters: 500,
      venue_type: "retail",
      phone: null,
      website: null,
      business_status: null,
      rating: null,
      user_ratings_total: null,
      editorial_summary: null,
      opening_hours: null,
      secondary_opening_hours: null,
      google_place_id: null,
      entry_mode: "auto",
      timezone_offset: null,
    });
  };

  const focusOnLocation = (location) => {
    if (overviewMapRef.current) {
      overviewMapRef.current.panTo({ lat: location.latitude, lng: location.longitude });
      overviewMapRef.current.setZoom(15);
    }
  };

  if (loading || !isLoaded) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-primary">Loading...</div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Map */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="h-[300px] rounded-lg overflow-hidden">
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={locations.length > 0 ? 8 : 4}
              onLoad={(map) => { overviewMapRef.current = map; }}
              options={mapOptions}
            >
              {filteredLocations.map(loc => (
                <div key={loc.id}>
                  <Marker position={{ lat: loc.latitude, lng: loc.longitude }} title={loc.name} />
                  <Circle center={{ lat: loc.latitude, lng: loc.longitude }} radius={loc.radius_meters}
                    options={{ strokeColor: "#3B82F6", strokeOpacity: 0.8, strokeWeight: 2, fillColor: "#3B82F6", fillOpacity: 0.15 }} />
                </div>
              ))}
            </GoogleMap>
          </div>
        </CardContent>
      </Card>

      {/* Search + Add */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search locations..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}><Plus className="mr-2 h-4 w-4" />Add Location</Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingLocation ? "Edit Location" : "Add Location"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Form Fields */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Location Name</Label>
                    <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Main Store" required />
                  </div>

                  {/* Auto-fill / Manual Toggle */}
                  {entryMode === "auto" ? (
                    <>
                      {/* Address Autocomplete */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <LocateFixed className="h-4 w-4 text-primary" />
                          Search Business or Address
                        </Label>
                        <AddressAutocomplete
                          defaultValue={addressSearch}
                          onPlaceSelect={handlePlaceSelect}
                          placeholder="Search for a business or address..."
                          searchTypes={["establishment"]}
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            Auto-fills address, opening hours, and business details
                          </p>
                          <button
                            type="button"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                            onClick={() => {
                              setEntryMode("manual");
                              setFormData(prev => ({
                                ...prev,
                                entry_mode: "manual",
                                google_place_id: null,
                                opening_hours: null,
                                phone: null,
                                website: null,
                                business_status: null,
                                rating: null,
                                user_ratings_total: null,
                                editorial_summary: null,
                              }));
                            }}
                          >
                            <Keyboard className="h-3 w-3" />
                            Enter address manually
                          </button>
                        </div>
                      </div>

                      {/* Address fields - read-only in auto mode after selection */}
                      <div className="space-y-2">
                        <Label>Street Address</Label>
                        <Input
                          value={formData.street_address}
                          readOnly={!!formData.google_place_id}
                          className={formData.google_place_id ? "bg-muted/50" : ""}
                          onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                          placeholder="123 Main Street"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Suburb</Label>
                          <Input
                            value={formData.suburb}
                            readOnly={!!formData.google_place_id}
                            className={formData.google_place_id ? "bg-muted/50" : ""}
                            onChange={(e) => setFormData(prev => ({ ...prev, suburb: e.target.value }))}
                            placeholder="Sydney"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Postcode</Label>
                          <Input
                            value={formData.postcode}
                            readOnly={!!formData.google_place_id}
                            className={formData.google_place_id ? "bg-muted/50" : ""}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder="2000"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Select value={formData.state} onValueChange={(v) => setFormData(prev => ({ ...prev, state: v }))} disabled={!!formData.google_place_id}>
                            <SelectTrigger className={formData.google_place_id ? "bg-muted/50" : ""}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>{australianStates.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Venue Type</Label>
                          <Select value={formData.venue_type} onValueChange={(v) => setFormData(prev => ({ ...prev, venue_type: v }))} disabled={!!formData.google_place_id}>
                            <SelectTrigger className={formData.google_place_id ? "bg-muted/50" : ""}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>{venueTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Business Details from Google Places - shown after selection */}
                      {formData.google_place_id && (
                        <div className="space-y-3 p-3 rounded-lg bg-secondary/30 border border-border">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Business Details (from Google)</p>

                          {/* Business Status */}
                          {formData.business_status && (
                            <div className="flex items-center gap-2">
                              {formData.business_status === "OPERATIONAL" ? (
                                <Badge variant="outline" className="text-green-600 border-green-600/30 bg-green-500/10">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Operational
                                </Badge>
                              ) : formData.business_status === "CLOSED_TEMPORARILY" ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-600/30 bg-amber-500/10">
                                  <AlertTriangle className="h-3 w-3 mr-1" /> Temporarily Closed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-red-600 border-red-600/30 bg-red-500/10">
                                  <XCircle className="h-3 w-3 mr-1" /> Permanently Closed
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Rating */}
                          {formData.rating && (
                            <div className="flex items-center gap-2 text-sm">
                              <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                              <span className="font-medium">{formData.rating}</span>
                              {formData.user_ratings_total && (
                                <span className="text-muted-foreground">({formData.user_ratings_total.toLocaleString()} reviews)</span>
                              )}
                            </div>
                          )}

                          {/* Opening Hours */}
                          {formData.opening_hours?.weekday_text?.length > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 text-sm font-medium">
                                <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                Opening Hours
                              </div>
                              <div className="grid gap-0.5 text-xs text-muted-foreground pl-5">
                                {formData.opening_hours.weekday_text.map((line, i) => (
                                  <span key={i}>{line}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Phone */}
                          {formData.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                              <span>{formData.phone}</span>
                            </div>
                          )}

                          {/* Website */}
                          {formData.website && (
                            <div className="flex items-center gap-2 text-sm">
                              <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="truncate text-primary">{formData.website}</span>
                            </div>
                          )}

                          {/* Editorial Summary */}
                          {formData.editorial_summary && (
                            <p className="text-xs text-muted-foreground italic">{formData.editorial_summary}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Manual Entry Mode */}
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Entering address manually</p>
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                          onClick={() => setEntryMode("auto")}
                        >
                          <Search className="h-3 w-3" />
                          Search for address
                        </button>
                      </div>

                      <div className="space-y-2">
                        <Label>Street Address</Label>
                        <Input
                          value={formData.street_address}
                          onChange={(e) => setFormData(prev => ({ ...prev, street_address: e.target.value }))}
                          placeholder="123 Main Street"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Suburb</Label>
                          <Input
                            value={formData.suburb}
                            onChange={(e) => setFormData(prev => ({ ...prev, suburb: e.target.value }))}
                            placeholder="Sydney"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Postcode</Label>
                          <Input
                            value={formData.postcode}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder="2000"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>State</Label>
                          <Select value={formData.state} onValueChange={(v) => setFormData(prev => ({ ...prev, state: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{australianStates.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Venue Type</Label>
                          <Select value={formData.venue_type} onValueChange={(v) => setFormData(prev => ({ ...prev, venue_type: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{venueTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Hidden fields */}
                  <input type="hidden" value={formData.radius_meters} />
                  <input type="hidden" value={formData.latitude} />
                  <input type="hidden" value={formData.longitude} />
                </div>

                {/* Google Map */}
                <div className="space-y-2">
                  <Label>Pin Location on Map</Label>
                  <p className="text-xs text-muted-foreground">Click on map to adjust pin position, or use address search above</p>
                  <div className="h-[400px] rounded-lg overflow-hidden border border-border">
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={13}
                      onClick={handleMapClick}
                      onLoad={(map) => { formMapRef.current = map; }}
                      options={mapOptions}>
                      <Marker position={{ lat: formData.latitude, lng: formData.longitude }} />
                      <Circle center={{ lat: formData.latitude, lng: formData.longitude }} radius={formData.radius_meters}
                        options={{ strokeColor: "#3B82F6", strokeOpacity: 0.8, strokeWeight: 2, fillColor: "#3B82F6", fillOpacity: 0.2 }} />
                    </GoogleMap>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editingLocation ? "Update" : "Create"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredLocations.map(loc => {
              const isPrimary = advertiser?.primary_location_id === loc.id;
              return (
                <div key={loc.id} className={`flex items-center justify-between p-4 hover:bg-muted/30 transition-colors ${isPrimary ? 'bg-primary/5' : ''}`}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="p-2.5 rounded-lg bg-primary/10 cursor-pointer hover:bg-primary/20" onClick={() => focusOnLocation(loc)}>
                      {isPrimary ? (
                        <Building2 className="h-5 w-5 text-primary" />
                      ) : (
                        <MapPin className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{loc.name}</h3>
                        {isPrimary && (
                          <Badge className="bg-primary text-primary-foreground text-xs">
                            <Star className="h-3 w-3 mr-1 fill-current" />
                            Primary
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs capitalize">{loc.venue_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{loc.street_address}</p>
                      <p className="text-sm text-muted-foreground">{loc.suburb}, {loc.state} {loc.postcode}</p>
                    </div>
                  </div>
                  <div className="hidden lg:flex flex-col items-end gap-1 text-xs text-muted-foreground px-4">
                    <div className="flex items-center gap-1">
                      <LocateFixed className="h-3 w-3" />
                      <span className="font-mono">{loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Navigation className="h-3 w-3" />
                      <span>{loc.radius_meters}m radius</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isPrimary && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 text-xs"
                        onClick={() => handleSetPrimary(loc.id)}
                        disabled={settingPrimary === loc.id}
                      >
                        {settingPrimary === loc.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Star className="h-3 w-3 mr-1" />
                            Set Primary
                          </>
                        )}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditDialog(loc)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(loc.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              );
            })}
            {filteredLocations.length === 0 && (
              <div className="p-12 text-center">
                <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No locations</h3>
                <p className="text-muted-foreground">Add a location for {advertiser?.business_name}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
